import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SchoolLevel, SubjectSlug } from '@jeraisonne/db';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/** Colonnes 0514 postérieures aux types générés → client générique. */
const db = supabase as unknown as SupabaseClient;

export interface ObjectiveTemplateInput {
  title: string;
  description?: string;
  type: string;
  content_type: 'exercises' | 'epreuves';
  subject?: SubjectSlug | null;
  allowed_levels: SchoolLevel[];
  /** L'élève choisit un pack (migration 0514). */
  requires_pack_selection: boolean;
  /** Programme épinglé (migration 0514) ; null = règle du type. */
  programme_id?: string | null;
  is_active: boolean;
  display_order: number;
  filters?: Record<string, unknown>;
  default_deadline?: string | null;
  /** Slug stable pour la landing publique. Auto-dérivé du titre si omis. */
  slug?: string;
}

function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-+|-+$)/g, '')
    .slice(0, 80);
}

export function useCreateObjectiveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ObjectiveTemplateInput) => {
      const slug = input.slug?.trim() || slugify(input.title) || `objectif-${Date.now()}`;
      const { data, error } = await db
        .from('objective_templates')
        .insert({
          ...input,
          slug,
          description: input.description ?? '',
          subject: input.subject ?? null,
          filters: (input.filters ?? {}) as Record<string, never>,
        })
        .select('id')
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-objective-templates'] });
    },
  });
}

export type UpdateObjectiveTemplateInput = Partial<ObjectiveTemplateInput> & { id: string };

export function useUpdateObjectiveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, filters, ...patch }: UpdateObjectiveTemplateInput) => {
      const { error } = await db
        .from('objective_templates')
        .update({
          ...patch,
          ...(filters !== undefined ? { filters: filters as Record<string, never> } : {}),
        })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['admin-objective-templates'] });
      qc.invalidateQueries({ queryKey: ['admin-objective-template', id] });
    },
  });
}

export function useDeleteObjectiveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { count } = await supabase
        .from('student_objectives')
        .select('id', { count: 'exact', head: true })
        .eq('objective_template_id', id)
        .eq('is_active', true)
        .then((r) => ({ count: r.count ?? 0 }));

      if (count > 0) {
        const { error } = await supabase
          .from('objective_templates')
          .update({ is_active: false })
          .eq('id', id);
        if (error) throw new Error(error.message);
      } else {
        const { error } = await supabase.from('objective_templates').delete().eq('id', id);
        if (error) throw new Error(error.message);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-objective-templates'] });
    },
  });
}
